/**
 * Phase 7F.1 — "Don't see your pet?" homepage request modal.
 * Phase 7F.2 — fixed a Theme Editor live-preview bug: <modal-dialog>'s
 * own connectedCallback() (assets/global.js, shared/untouched) moves
 * itself to be a direct child of <body> on connect. When Theme Editor
 * re-renders this section after an unsaved settings change, the fresh
 * modal-dialog (with the newly-edited heading/copy) gets appended
 * AFTER the previous instance, which is never removed — two elements
 * end up sharing the same id, and <modal-opener>'s
 * document.querySelector('#id') always resolves the FIRST (stale) one,
 * so edited text appeared not to take effect until a full page
 * reload. removeStaleModalDuplicates() below removes any earlier
 * same-id instance whenever a form initializes, and the
 * shopify:section:load listener (the same re-init pattern assets/
 * bpf-homepage-hero.js already uses for this exact section) makes
 * sure a freshly-rendered instance actually gets initialized at all —
 * previously this file only ever ran once, on first page load.
 *
 * On the homepage, only ever loaded when section.settings.
 * enable_pet_request is true (see sections/bpf-homepage-hero.liquid)
 * — when the setting is off, this file is never linked at all there,
 * so nothing here can run for that page. Every selector below targets
 * markup driven entirely by data-pet-request-* attributes, so even a
 * defensive/cached load of this file where none of that markup exists
 * finds zero matching elements and does nothing.
 *
 * Phase 7H.1 — this file is no longer homepage-exclusive: the Builder
 * (sections/bpf-memorial-designer.liquid) unconditionally links this
 * exact same file for its own "Don't see your pet?" (Choose Your
 * Companion) instance, unmodified in every respect except one additive
 * property below (request_source) — already instance-based/data-
 * attribute-driven via .closest() lookups with a data-pet-request-
 * initialized guard per form, so a second independent instance on the
 * same page needed zero structural changes to function correctly.
 *
 * Open/close, focus trap, Escape-to-close, background-scroll lock, and
 * focus-return-to-opener are ALL handled already by the theme's
 * existing <modal-dialog>/<modal-opener> custom elements (assets/
 * global.js) — this file only owns: conditional Dog/Cat vs Other
 * fields, validation, Klaviyo identify+track, success/error state, and
 * resetting the form when the modal closes.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function trim(value) {
    return (value || '').replace(/^\s+|\s+$/g, '');
  }

  function removeStaleModalDuplicates(id) {
    if (!id || !window.CSS || typeof window.CSS.escape !== 'function') return;
    var matches = document.querySelectorAll('#' + window.CSS.escape(id));
    for (var i = 0; i < matches.length - 1; i++) {
      if (matches[i].parentNode) matches[i].parentNode.removeChild(matches[i]);
    }
  }

  function initPetRequestForm(form) {
    if (form.hasAttribute('data-pet-request-initialized')) return;
    var dialogRoot = form.closest('.bpf-pet-request-modal__dialog');
    var modal = form.closest('modal-dialog');
    if (!dialogRoot || !modal) return;

    form.setAttribute('data-pet-request-initialized', 'true');
    removeStaleModalDuplicates(modal.id);

    var formState = dialogRoot.querySelector('[data-pet-request-form-state]');
    var successState = dialogRoot.querySelector('[data-pet-request-success-state]');
    var petTypeInputs = form.querySelectorAll('[data-pet-request-pet-type]');
    var breedField = form.querySelector('[data-pet-request-breed-field]');
    var breedInput = form.querySelector('[data-pet-request-breed-input]');
    var otherField = form.querySelector('[data-pet-request-other-field]');
    var otherInput = form.querySelector('[data-pet-request-other-input]');
    var emailInput = form.querySelector('[data-pet-request-email-input]');
    var submitButton = form.querySelector('[data-pet-request-submit]');
    var submitLabel = form.querySelector('[data-pet-request-submit-label]');
    var submitError = form.querySelector('[data-pet-request-submit-error]');
    var closeSuccessButton = dialogRoot.querySelector('[data-pet-request-close-success]');
    // Phase 7H.1 — this form is no longer homepage-only: the Builder's
    // own "Don't see your pet?" (Choose Your Companion) reuses this
    // exact file unmodified in every other respect. data-pet-request-
    // source distinguishes the two for Klaviyo's request_source event
    // property; defaulting to 'homepage' when absent means the
    // homepage's own markup needs no attribute added at all (it was
    // the only instance that existed before this phase), while the
    // Builder's markup sets it explicitly to 'builder_companion'.
    var requestSource = form.getAttribute('data-pet-request-source') || 'homepage';

    var defaultSubmitLabel = submitLabel ? submitLabel.textContent : 'Submit Request';
    var isSubmitting = false;

    function clearFieldError(name) {
      var el = form.querySelector('[data-pet-request-error="' + name + '"]');
      if (el) el.hidden = true;
    }

    function setFieldError(name, input) {
      var el = form.querySelector('[data-pet-request-error="' + name + '"]');
      if (el) el.hidden = false;
      if (input) input.setAttribute('aria-invalid', 'true');
    }

    function clearAllErrors() {
      form.querySelectorAll('[data-pet-request-error]').forEach(function (el) {
        el.hidden = true;
      });
      form.querySelectorAll('[aria-invalid]').forEach(function (el) {
        el.removeAttribute('aria-invalid');
      });
      if (submitError) submitError.hidden = true;
    }

    function currentPetType() {
      var checked = form.querySelector('[data-pet-request-pet-type]:checked');
      return checked ? checked.value : '';
    }

    function syncConditionalFields() {
      var petType = currentPetType();

      if (petType === 'Dog' || petType === 'Cat') {
        breedField.hidden = false;
        otherField.hidden = true;
        breedInput.setAttribute('required', 'required');
        otherInput.removeAttribute('required');
        otherInput.value = '';
        clearFieldError('other_pet_type');
      } else if (petType === 'Other') {
        breedField.hidden = true;
        otherField.hidden = false;
        otherInput.setAttribute('required', 'required');
        breedInput.removeAttribute('required');
        breedInput.value = '';
        clearFieldError('breed');
      } else {
        breedField.hidden = true;
        otherField.hidden = true;
        breedInput.removeAttribute('required');
        otherInput.removeAttribute('required');
      }

      clearFieldError('pet_type');
    }

    petTypeInputs.forEach(function (input) {
      input.addEventListener('change', syncConditionalFields);
    });

    function resetForm() {
      isSubmitting = false;
      form.reset();
      breedField.hidden = true;
      otherField.hidden = true;
      breedInput.removeAttribute('required');
      otherInput.removeAttribute('required');
      breedInput.value = '';
      otherInput.value = '';
      clearAllErrors();
      if (submitButton) submitButton.disabled = false;
      if (submitLabel) submitLabel.textContent = defaultSubmitLabel;
      if (formState) formState.hidden = false;
      if (successState) successState.hidden = true;
    }

    function showSuccess() {
      if (formState) formState.hidden = true;
      if (successState) {
        successState.hidden = false;
        var heading = successState.querySelector('.bpf-pet-request-modal__heading');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus();
        }
      }
    }

    function submissionContext() {
      return window.Shopify && window.Shopify.designMode ? 'theme_editor' : 'storefront';
    }

    function klaviyoQueueAvailable() {
      return !!(window.klaviyo && typeof window.klaviyo.push === 'function');
    }

    function submitToKlaviyo(payload) {
      if (!klaviyoQueueAvailable()) return false;
      try {
        window.klaviyo.push(['identify', { email: payload.email }]);
        window.klaviyo.push([
          'track',
          'Pet Memorial Request',
          {
            animal_type: payload.animal_type,
            requested_breed: payload.requested_breed,
            requested_other_pet_type: payload.requested_other_pet_type,
            submission_context: payload.submission_context,
            request_source: payload.request_source
          }
        ]);
        return true;
      } catch (err) {
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[bpf-homepage-pet-request] Klaviyo tracking failed', err);
        }
        return false;
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (isSubmitting) return;

      clearAllErrors();

      var petType = currentPetType();
      var breed = trim(breedInput.value);
      var otherType = trim(otherInput.value);
      var email = trim(emailInput.value);
      var firstInvalid = null;

      if (!petType) {
        setFieldError('pet_type');
        firstInvalid = firstInvalid || petTypeInputs[0];
      }
      if (petType === 'Dog' || petType === 'Cat') {
        if (!breed) {
          setFieldError('breed', breedInput);
          firstInvalid = firstInvalid || breedInput;
        }
      }
      if (petType === 'Other') {
        if (!otherType) {
          setFieldError('other_pet_type', otherInput);
          firstInvalid = firstInvalid || otherInput;
        }
      }
      if (!email || !EMAIL_RE.test(email)) {
        setFieldError('email', emailInput);
        firstInvalid = firstInvalid || emailInput;
      }

      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      isSubmitting = true;
      if (submitButton) submitButton.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Submitting…';

      var payload = {
        animal_type: petType,
        requested_breed: petType === 'Dog' || petType === 'Cat' ? breed : '',
        requested_other_pet_type: petType === 'Other' ? otherType : '',
        submission_context: submissionContext(),
        request_source: requestSource,
        email: email
      };

      var accepted = submitToKlaviyo(payload);

      if (!accepted) {
        isSubmitting = false;
        if (submitButton) submitButton.disabled = false;
        if (submitLabel) submitLabel.textContent = defaultSubmitLabel;
        if (submitError) submitError.hidden = false;
        return;
      }

      showSuccess();
    });

    if (closeSuccessButton) {
      closeSuccessButton.addEventListener('click', function () {
        modal.hide();
      });
    }

    if (window.MutationObserver) {
      var wasOpen = modal.hasAttribute('open');
      var observer = new MutationObserver(function () {
        var isOpen = modal.hasAttribute('open');
        if (wasOpen && !isOpen) resetForm();
        wasOpen = isOpen;
      });
      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    syncConditionalFields();
  }

  document.querySelectorAll('[data-pet-request-form]').forEach(initPetRequestForm);

  // Theme Editor section re-render safety: a fresh section instance
  // (new DOM node) after a settings/block edit needs its own listeners
  // bound again — never assumed to already have them, the same
  // principle assets/bpf-homepage-hero.js already applies for this
  // same section. Deliberately queries the WHOLE document (not
  // event.target) — unlike that file's own elements, <modal-dialog>'s
  // connectedCallback (assets/global.js) has already relocated the
  // freshly-rendered modal (and this form nested inside it) to be a
  // child of <body> by the time this event fires, so it is no longer
  // a descendant of the section wrapper at all. initPetRequestForm's
  // own data-pet-request-initialized guard skips any form already set
  // up, so this never double-binds listeners on an existing instance.
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-pet-request-form]').forEach(initPetRequestForm);
  });
})();
